import { Directive, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { MatTableDataSourceBackendExtended } from "../../../../../shared/classes/datasource-extended";
import { IDataSourceBackendParam } from "../../interfaces/samples.interface";

@Directive()
export abstract class AbstractBackendTablePage<T> implements OnInit, OnDestroy {
  public filters: {
    sampleId: string;
    firstname: string;
    name: string;
    source: string;
    cancerType: string;
    hasRnaSeq: string;
    hasMutation: string;
    metadataCompletion: string;
    uploadDate: string;
    sortBy?: "sampleID" | "createdAt" | "completionRate" | "source" | "cancerType" | "firstname" | "name";
    sortOrder?: "asc" | "desc";
  } = {
    sampleId: "",
    firstname: "",
    name: "",
    source: "",
    cancerType: "",
    hasRnaSeq: "",
    hasMutation: "",
    metadataCompletion: "",
    uploadDate: "",
  };
  public columns: string[] = [];

  public actionOnMultiple = false;

  public totalCount = 0;
  public nbPages = 0;
  public displayedStart = 0;
  public displayedEnd = 0;
  public currentPage = 1;
  public currentPageSize = 10;

  public loading = false;

  public dataSource!: MatTableDataSourceBackendExtended<T>;
  protected onDestroy$ = new Subject<void>();

  // ---- Abstract methods to implements ----
  /** Initialize table data source */
  public abstract initDataSource(): void;

  /** Initialize sources data */
  public abstract initSources(): void;

  /**
   * Initializes data source and loads first page.
   */
  async ngOnInit(): Promise<void> {
    this.initDataSource();
    this.initSources();
    await this.loadData();
  }

  /**
   * Cleanup subscriptions on destroy.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Converts date from YYYY-MM-DD... to DD/MM/YYYY.
   * @param date Date string in format YYYY-MM-DD
   * @return Formatted date string in DD/MM/YYYY
   */
  private formatDate(date: string): string {
    const [year, month, day] = date.split("-");

    return `${day}/${month}/${year}`;
  }

  /**
   * Builds API query parameters from current filters and pagination.
   * @return Object containing formatted backend query parameters
   */
  protected buildParams(): IDataSourceBackendParam {
    const params: IDataSourceBackendParam = {
      page: this.currentPage,
      pageSize: this.currentPageSize,
    };

    if (this.filters.sortBy) {
      params.sortBy = this.filters.sortBy;
      params.sortOrder = this.filters.sortOrder;
    }

    if (this.filters.sampleId) {
      params.sampleId = this.filters.sampleId;
    }

    if (this.filters.firstname) {
      params.firstname = this.filters.firstname;
    }

    if (this.filters.name) {
      params.name = this.filters.name;
    }

    if (this.filters.source) {
      params.source = Number(this.filters.source);
    }

    if (this.filters.cancerType) {
      params.cancerType = Number(this.filters.cancerType);
    }

    if (this.filters.hasRnaSeq !== "") {
      params.hasRnaSeq = this.filters.hasRnaSeq === "true";
    }

    if (this.filters.hasMutation !== "") {
      params.hasMutation = this.filters.hasMutation === "true";
    }

    if (this.filters.metadataCompletion) {
      params.metadataCompletion = Number(this.filters.metadataCompletion);
    }

    if (this.filters.uploadDate) {
      params.uploadDate = this.formatDate(this.filters.uploadDate);
    }

    return params;
  }

  /**
   * Loads data from backend using current filters and pagination.
   * @return Promise resolved when data loading is complete
   */
  public async loadData(): Promise<void> {
    this.loading = true;
    const params = this.buildParams();
    await this.dataSource.loadData(params);
    this.totalCount = this.dataSource.getDataCount();
    this.updatePaginationInfo();
    this.loading = false;
  }

  /**
   * Resets all filters to default values and reloads data.
   */
  public clearFilters(): void {
    this.filters = {
      sampleId: "",
      firstname: "",
      name: "",
      source: "",
      cancerType: "",
      hasRnaSeq: "",
      hasMutation: "",
      metadataCompletion: "",
      uploadDate: "",
    };

    this.loadData();
  }

  /**
   * Updates sorting state for a given column and reloads data.
   * @param column Column identifier to sort by
   */
  public changeSort(column: "sampleID" | "createdAt" | "completionRate" | "source" | "cancerType" | "firstname" | "name"): void {
    if (this.filters.sortBy === column) {
      if (this.filters.sortOrder === "asc") this.filters.sortOrder = "desc";
      else if (this.filters.sortOrder === "desc") {
        this.filters.sortOrder = undefined;
        this.filters.sortBy = undefined;
      } else this.filters.sortOrder = "asc";
    } else {
      this.filters.sortBy = column;
      this.filters.sortOrder = "asc";
    }

    this.loadData();
  }

  /**
   * Updates pagination display values (start index, end index, total pages).
   */
  private updatePaginationInfo(): void {
    this.displayedStart = this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.currentPageSize + 1;
    this.displayedEnd = Math.min(this.currentPage * this.currentPageSize, this.totalCount);
    this.nbPages = Math.ceil(this.totalCount / this.currentPageSize);
  }

  /**
   * Changes current page and reloads data.
   * @param size Page number
   */
  public changePage(page: number): void {
    this.currentPage = page;
    this.loadData();
  }

  /**
   * Changes number of items per page, resets to first page and reloads data.
   * @param size Number of items per page
   */
  public changePageSize(size: number): void {
    this.currentPageSize = size;
    this.currentPage = 1;
    this.loadData();
  }
}
